import type {
  SaveWeeklyKpiRequest,
  WeeklyKpiRecord,
  WeeklyKpiResponse,
  WeeklyReportResponse,
  WeeklyReportWeekRequest,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/**
 * The weekly report and KPI form for a project.
 *
 * Generating is a write and costs a model pass of tens of seconds; reading is
 * free and never touches the AI. The two are separate calls for that reason —
 * opening the tab must not spend a pass, which is why nothing here generates
 * on a read.
 */

/**
 * The project key, encoded.
 *
 * Only the key is lifted out, never the prefix: `auth.test.ts` reads these
 * files looking for a literal `/api/` at every call site, because a path that
 * loses the global prefix is a 404 nobody sees until it is deployed. A helper
 * that returned the whole base would hide exactly what that guard is reading.
 */
function key(projectKey: string): string {
  return encodeURIComponent(projectKey)
}

/**
 * Build the report for one week and keep it.
 *
 * @param projectKey Backlog key of the project.
 * @param body       The week, and the language to write in.
 * @returns The stored report, the same shape a later read gives.
 * @throws ApiError when the week is refused or the AI service is unreachable.
 */
export async function generateWeeklyReport(
  projectKey: string,
  body: WeeklyReportWeekRequest
): Promise<WeeklyReportResponse> {
  try {
    const { data } = await apiClient.post<WeeklyReportResponse>(
      `/api/projects/${key(projectKey)}/weekly-report`,
      body
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedGenerateWeeklyReport)
  }
}

/** The stored report for a week; `report` is null when nobody has generated it. */
export async function fetchWeeklyReport(
  projectKey: string,
  weekStart: string
): Promise<WeeklyReportResponse> {
  try {
    const { data } = await apiClient.get<WeeklyReportResponse>(
      `/api/projects/${key(projectKey)}/weekly-report`,
      { params: { weekStart } }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadWeeklyReport)
  }
}

/**
 * Draft the KPI form for a week.
 *
 * Nothing is stored by this call — the draft exists to be corrected, and
 * {@link saveWeeklyKpi} is what keeps the corrected version.
 */
export async function draftWeeklyKpi(
  projectKey: string,
  body: WeeklyReportWeekRequest
): Promise<WeeklyKpiRecord> {
  try {
    const { data } = await apiClient.post<WeeklyKpiRecord>(
      `/api/projects/${key(projectKey)}/weekly-report/kpi-draft`,
      body
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedGenerateWeeklyKpi)
  }
}

/** Keep the KPI form as the reader left it. */
export async function saveWeeklyKpi(
  projectKey: string,
  body: SaveWeeklyKpiRequest
): Promise<WeeklyKpiResponse> {
  try {
    const { data } = await apiClient.put<WeeklyKpiResponse>(
      `/api/projects/${key(projectKey)}/weekly-report/kpi`,
      body
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveWeeklyKpi)
  }
}

/** The saved KPI form for a week; `kpi` is null when nobody has saved one. */
export async function fetchWeeklyKpi(
  projectKey: string,
  weekStart: string
): Promise<WeeklyKpiResponse> {
  try {
    const { data } = await apiClient.get<WeeklyKpiResponse>(
      `/api/projects/${key(projectKey)}/weekly-report/kpi`,
      { params: { weekStart } }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadWeeklyReport)
  }
}

/**
 * Download the week as a PowerPoint deck.
 *
 * Asks for a blob: read as text the file arrives corrupted while still looking
 * like a success. The name comes from the server, because it encodes the week
 * and two sides inventing it separately is how they come to disagree.
 *
 * @returns The file and the name to save it under.
 */
export async function downloadWeeklySlides(
  projectKey: string,
  body: WeeklyReportWeekRequest
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await apiClient.post<Blob>(
      `/api/projects/${key(projectKey)}/weekly-report/slides`,
      body,
      { responseType: 'blob' }
    )
    return {
      blob: response.data,
      filename:
        filenameFromDisposition(response.headers['content-disposition']) ??
        `Weekly Report (${body.weekStart} - ${body.weekEnd}).pptx`,
    }
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedExportWeeklySlides)
  }
}

/** The quoted `filename="…"` out of a content-disposition header. */
function filenameFromDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null
  const quoted = /filename\*?="([^"]+)"/i.exec(header)
  if (quoted) return quoted[1]
  const bare = /filename\*?=([^;]+)/i.exec(header)
  return bare ? bare[1].trim() : null
}
